import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate } from 'react-router-dom';

import { useSettings } from '../context/SettingsContext'
import { Nav } from './partials/Nav';
import { API_URL } from './Constants';

const TransactionReport = () => {
  const settings = useSettings();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [lots, setLots] = useState([]);
  const [selectedLot, setSelectedLot] = useState('');
  const [transactions, setTransactions] = useState(null);
  const [error, setError] = useState(null);
  
  // Initialize dates to current month
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
  });
  
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().split('T')[0];
  });

  useEffect(() => {
    document.title = 'Transaction Breakdown | ValParx Admin'
  }, []);

  // Fetch lots on component mount
  useEffect(() => {
    if (settings.userToken) {
      fetchLots();
    }
  }, [settings.userToken]);

  const fetchLots = () => {
    const token = settings.userToken;
    const myHeaders = new Headers();
    myHeaders.append("vp-session-token", token);

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      redirect: 'follow'
    };

    fetch(API_URL + "/gettransactionlots", requestOptions)
      .then(response => response.json())
      .then(result => {
        if (result.code === 200) {
          setLots(result.lots);
        } else {
          setError('Failed to fetch lots');
        }
      })
      .catch(error => {
        console.error('error', error);
        setError('Failed to fetch lots');
      });
  };

  const fetchTransactions = () => {
    if (!selectedLot) {
      setError('Please select a lot');
      return;
    }

    setLoading(true);
    setError(null);
    
    const token = settings.userToken;
    const myHeaders = new Headers();
    myHeaders.append("vp-session-token", token);
    myHeaders.append("Content-Type", "application/x-www-form-urlencoded");

    const urlencoded = new URLSearchParams();
    urlencoded.append("lot_id", selectedLot);
    urlencoded.append("start_date", startDate);
    urlencoded.append("end_date", endDate);

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: urlencoded,
      redirect: 'follow'
    };

    fetch(API_URL + "/gettransactionsbylot", requestOptions)
      .then(response => response.json())
      .then(result => {
        if (result.code === 200) {
          setTransactions(result);
        } else {
          setError(result.message || 'Failed to fetch transactions');
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('error', error);
        setError('Failed to fetch transaction data');
        setLoading(false);
      });
  };

  const handleGenerateReport = (e) => {
    e.preventDefault();
    fetchTransactions();
  };

  const handleRowClick = (transactionId) => {
    navigate(`/reports/transactions/${transactionId}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className='min-h-screen flex items-start justify-center py-12 px-4 sm:px-6 lg:px-8'>
      <Nav />
      <div className="max-w-7xl w-full space-y-8 pt-20">
        <div className='flex justify-between items-center mb-8'>
          <div>
            <Link to="/reports" className="text-accent hover:text-accent-dark mb-2 inline-flex items-center">
              <FontAwesomeIcon icon="fa-arrow-left fa-solid" className='mr-2 text-sm' />
              Back to Reports
            </Link>
            <h2 className='mb-0'>Transaction Breakdown by Lot</h2>
          </div>
        </div>

        {/* Filters Form */}
        <form onSubmit={handleGenerateReport} className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <label htmlFor="lot_select" className="block text-sm font-medium text-gray-700 mb-2">
                Select Lot
              </label>
              <select
                id="lot_select"
                value={selectedLot}
                onChange={(e) => setSelectedLot(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                required
              >
                <option value="">-- Select a Lot --</option>
                {lots.map((lot) => (
                  <option key={lot.id} value={lot.id}>
                    {lot.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                id="start_date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                required
              />
            </div>
            <div className="flex-1 min-w-[180px]">
              <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                id="end_date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                required
              />
            </div>
            <div>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-accent text-white font-medium rounded-md hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon="fa-solid fa-spinner" className='mr-2 fa-spin' />
                    Loading...
                  </>
                ) : (
                  'Generate Report'
                )}
              </button>
            </div>
          </div>
        </form>

        {/* Error Display */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        {/* Transactions Table */}
        {transactions && !loading && (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {transactions.count} Transaction{transactions.count !== 1 ? 's' : ''} Found
                <span className="text-sm font-normal text-gray-600 ml-2">
                  ({new Date(transactions.start_date).toLocaleDateString()} - {new Date(transactions.end_date).toLocaleDateString()})
                </span>
              </h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date Created
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vehicle Plate
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Driver
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Closing Driver
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Method
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.transactions.length > 0 ? (
                    transactions.transactions.map((transaction, index) => (
                      <tr 
                        key={transaction.id} 
                        onClick={() => handleRowClick(transaction.id)}
                        className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 cursor-pointer transition-colors`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(transaction.time_created)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transaction.customer_name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transaction.vehicle_plate || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transaction.driver || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transaction.closing_driver || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transaction.payment_method_text}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <FontAwesomeIcon icon="fa-arrow-right fa-solid" className='text-accent' />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                        No transactions found for the selected criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TransactionReport;
